import type { PricingList } from 'api/types/pricing.types';
import { useFormattedDecimal } from 'hooks/useFormattedDecimal';
import { selectIsShoeCareSelected } from 'pages/newOrder/newOrder.selectors';
import React, { useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useCartEstimator, { useItemQuantity } from 'store/cart-estimator';
import styled from 'styled-components';

import AddButton from './addButton';

const StyledH3 = styled.h3<{ hasDiscountedPice?: boolean }>`
  font-weight: ${({ hasDiscountedPice = false }) => (hasDiscountedPice ? 300 : 500)};
  font-size: ${({ theme }) => theme.components?.estimatedBillBottomSheet?.addItemRow.title.fontSize ?? 14}px;
  margin-bottom: 0px;
  color: ${({ theme }) => theme.components?.estimatedBillBottomSheet?.addItemRow.title.textColor};
  text-decoration: ${({ hasDiscountedPice = false }) => (hasDiscountedPice ? 'line-through' : null)};
`;

const StyledH4 = styled.h4<{ hasDiscountedPice?: boolean }>`
  font-weight: 300;
  font-size: ${({ theme }) => theme.components?.estimatedBillBottomSheet?.addItemRow.subText.fontSize ?? 14}px;
  color: ${({ theme, hasDiscountedPice = false }) =>
    hasDiscountedPice
      ? theme.components?.estimatedBillBottomSheet?.addItemRow.subText.textColor.discounted
      : theme.components?.estimatedBillBottomSheet?.addItemRow.subText.textColor.normal};
  text-decoration: ${({ hasDiscountedPice = false }) => (hasDiscountedPice ? 'line-through' : null)};
`;

const StyledH4Discounted = styled.h4`
  font-weight: 300;
  color: ${({ theme }) => theme.text.success};
`;

const StyledContainer = styled(Container)<{ padding: number; noPadding?: boolean }>`
  padding-left: ${({ padding }) => padding}px;
  display: flex;
  padding-right: ${({ noPadding = false }) => (noPadding ? '0px' : undefined)};
  align-items: center;
`;

const StyledDiv = styled(Row)<{ serviceType?: PricingList.ServiceTypes }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 7px;
  border-radius: 100px;
  background-color: ${({ serviceType }) => (serviceType === 'WASH_FOLD' ? '#E8EAFF' : '#FFE8FE')};
  color: ${({ theme }) => theme.text.color};
  margin-left: 5px;
`;

interface Props {
  data: PricingList.ItemType;
  currency?: string;
  serviceType: PricingList.ServiceTypes;
  noPadding?: boolean;
  showHighlightedPrice?: boolean;
  isAddButtonDisabled?: boolean;
  category: string;
}

const AddItemRow: React.FC<Props> = ({
  data,
  currency,
  serviceType,
  category,
  noPadding = false,
  showHighlightedPrice = false,
  isAddButtonDisabled = false,
}) => {
  const { t } = useTranslation();
  const add = useCartEstimator.use.add();
  const remove = useCartEstimator.use.remove();
  const isShoeCareSelected = useSelector(selectIsShoeCareSelected);
  const clearCategory = useCartEstimator.use.clearCategory();
  const itemQuantity = useItemQuantity(serviceType, data.key);
  const { toFixed } = useFormattedDecimal();

  React.useEffect(() => {
    if (isAddButtonDisabled) {
      clearCategory(category, serviceType);
    }
  }, [category, clearCategory, isAddButtonDisabled, serviceType]);

  const NormalPriceText = useMemo(() => {
    if (showHighlightedPrice) return null;
    return (
      <Row noGutters>
        <StyledH4 className="pr-1 mb-0" hasDiscountedPice={data.discountedPrice !== undefined}>
          {currency} {toFixed(data.price, 2)}
        </StyledH4>
        {data.discountedPrice && (
          <StyledH4Discounted>
            {currency} {toFixed(data.discountedPrice, 2)}
          </StyledH4Discounted>
        )}
      </Row>
    );
  }, [currency, data.discountedPrice, data.price, showHighlightedPrice]);

  return (
    <StyledContainer className="mb-2" padding={noPadding ? 0 : data ? 40 : 24} noPadding={noPadding}>
      {!showHighlightedPrice ? (
        <Col className="p-0">
          <StyledH3>{data.name}</StyledH3>
          {!isShoeCareSelected && NormalPriceText}
        </Col>
      ) : (
        <StyledDiv serviceType={serviceType}>
          <StyledH3 hasDiscountedPice={data.discountedPrice !== undefined}>
            {currency} {toFixed(data.price, 2)}
          </StyledH3>
          {data.discountedPrice && (
            <StyledH3 className="mb-0 pl-1">
              {currency} {toFixed(data.discountedPrice, 2)}
            </StyledH3>
          )}
          <StyledH3 className="mb-0 pl-1">{t('common.perBag').toLowerCase()}*</StyledH3>
        </StyledDiv>
      )}
      <Col className="d-flex justify-content-end p-0">
        {!isShoeCareSelected ? (
          <AddButton
            item={{ ...data, quantity: itemQuantity, category }}
            serviceType={serviceType}
            isDisabled={isAddButtonDisabled}
            add={add}
            remove={remove}
          />
        ) : (
          NormalPriceText
        )}
      </Col>
    </StyledContainer>
  );
};

export default AddItemRow;
